import React from "react";

import {
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	TextField,
	TextFieldProps,
	Typography
} from "@mui/material";
import { useFormik } from "formik";
// "mobile" metadata does strict digit-pattern validation; the default "min" bundle only checks
// plausible length, so e.g. a truncated or malformed number could still pass as "valid".
import PhoneInputWithoutTypes from "react-phone-number-input/input-mobile";
import { isValidPhoneNumber } from "react-phone-number-input/mobile";
import ButtonComponent from "../../../Components/ButtonComponent";
import { createAffiliateSchema } from "./schema";
import { AffiliateUser, IPayOutType, PayoutMethod } from "../../../store/services/affiliate/dto/affiliate.dto";

// react-phone-number-input's public types only allow standard <input> props on `inputComponent`,
// but it forwards any extra props it doesn't handle straight through - widen the type so TextField props go through too.
type PhoneNumberInputProps = Omit<TextFieldProps, "value" | "onChange"> & {
	country?: string;
	international?: boolean;
	value?: string;
	onChange?: (value?: string) => void;
	inputComponent?: React.ComponentType<any>;
};
const PhoneInput = PhoneInputWithoutTypes as unknown as React.ForwardRefExoticComponent<
	PhoneNumberInputProps & React.RefAttributes<HTMLInputElement>
>;

const PhoneNumberTextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => (
	<TextField {...props} inputRef={ref} />
));

const CreateAffiliateModal = ({
	open,
	onClose,
	onCreate,
	isCreatingAffiliate
}: {
	open: boolean;
	onClose: () => void;
	onCreate: (payload: Partial<AffiliateUser>) => void;
	isCreatingAffiliate: boolean;
}) => {
	const { values, errors, handleChange, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			businessName: "",
			type: IPayOutType.BANK,
			holderName: "",
			bankName: "",
			accountNumber: "",
			walletMobileNumber: "",
			agreedToRules: false
		},
		validationSchema: createAffiliateSchema,
		onSubmit: formValues => {
			console.log(formValues);
			const payoutMethod: PayoutMethod =
				formValues.type === IPayOutType.BANK
					? {
							type: IPayOutType.BANK,
							holderName: formValues.holderName,
							bankName: formValues.bankName,
							accountNumber: formValues.accountNumber
						}
					: {
							type: IPayOutType.WALLET,
							holderName: formValues.holderName,
							walletMobileNumber: formValues.walletMobileNumber
						};

			onCreate({ businessName: formValues.businessName, payoutMethod });
		}
	});

	const handleNumericChange = (field: "accountNumber") => (e: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValue(field, e.target.value.replace(/\D/g, ""));
	};

	const isWalletNumberInvalid =
		values.type === IPayOutType.WALLET && !isValidPhoneNumber(values.walletMobileNumber || "");

	return (
		<Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
			<DialogTitle>Become an Affiliate</DialogTitle>
			<form onSubmit={handleSubmit}>
				<DialogContent>
					<Box display={"flex"} flexDirection={"column"} gap={2}>
						<TextField
							name={"businessName"}
							label={"Business Name"}
							value={values.businessName}
							onChange={handleChange}
							error={!!errors.businessName}
							helperText={errors.businessName}
						/>
						<Typography variant={"subtitle2"} fontWeight={"bold"} mt={1}>
							Payout Details
						</Typography>
						<RadioGroup row value={values.type} onChange={e => setFieldValue("type", e.target.value)}>
							<FormControlLabel value={IPayOutType.BANK} control={<Radio />} label={"Bank Account"} />
							<FormControlLabel value={IPayOutType.WALLET} control={<Radio />} label={"Instapay / Mobile Wallet"} />
						</RadioGroup>
						<TextField
							name={"holderName"}
							label={"Account Holder Name"}
							value={values.holderName}
							onChange={handleChange}
							error={!!errors.holderName}
							helperText={errors.holderName}
						/>
						{values.type === IPayOutType.BANK ? (
							<>
								<TextField
									name={"bankName"}
									label={"Bank Name"}
									value={values.bankName}
									onChange={handleChange}
									error={!!errors.bankName}
									helperText={errors.bankName}
								/>
								<TextField
									name={"accountNumber"}
									label={"Account Number"}
									value={values.accountNumber}
									onChange={handleNumericChange("accountNumber")}
									error={!!errors.accountNumber}
									helperText={errors.accountNumber}
									inputProps={{ inputMode: "numeric" }}
								/>
							</>
						) : (
							<PhoneInput
								country={"EG"}
								value={values.walletMobileNumber}
								onChange={value => setFieldValue("walletMobileNumber", value || "")}
								inputComponent={PhoneNumberTextField}
								label={"Wallet Mobile Number"}
								placeholder={"e.g. 01012345678"}
								error={!!errors.walletMobileNumber}
								helperText={errors.walletMobileNumber}
							/>
						)}
						<Box>
							<FormControlLabel
								control={
									<Checkbox
										checked={values.agreedToRules}
										onChange={e => setFieldValue("agreedToRules", e.target.checked)}
									/>
								}
								label={
									<Typography variant={"body2"}>
										I have read, understand, and agree to the{" "}
										<a href={"/affiliate-policy"} target={"_blank"} rel={"noreferrer"}>
											Affiliate Program Rules
										</a>
									</Typography>
								}
							/>
							{!!errors.agreedToRules && <FormHelperText error>{errors.agreedToRules}</FormHelperText>}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<ButtonComponent variant={"text"} color={"inherit"} loading={false} onClick={onClose} text={"Cancel"} />
					<ButtonComponent
						variant={"contained"}
						color={"primary"}
						type={"submit"}
						loading={isCreatingAffiliate}
						disabled={isCreatingAffiliate || isWalletNumberInvalid || !values.agreedToRules}
						text={"Create"}
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default CreateAffiliateModal;
